<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Button from 'next/button/Button.vue';
import TicketsAPI from 'dashboard/api/tickets.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  filterOptions: {
    type: Object,
    default: () => ({
      categories: [],
      explotaciones: [],
      localidades: [],
      colonias: [],
    }),
  },
  showTeamFilter: { type: Boolean, default: false },
  teamOptions: { type: Array, default: () => [] },
  initialValues: {
    type: Object,
    default: () => ({}),
  },
  popupStyle: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['apply', 'close']);

const { t } = useI18n();

const draft = ref(emptyDraft());
const scopedColonias = ref([]);
const isLoadingScoped = ref(false);

const { isCloudFeatureEnabled } = useAccount();
const isContractsEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CONTRACTS)
);

const statusOptions = [
  { id: '', name: 'TICKETS.FILTERS.ALL_STATUSES' },
  { id: 'open', name: 'TICKETS.STATUS.OPEN' },
  { id: 'in_progress', name: 'TICKETS.STATUS.IN_PROGRESS' },
  { id: 'escalated', name: 'TICKETS.STATUS.ESCALATED' },
  { id: 'waiting_client', name: 'TICKETS.STATUS.WAITING_CLIENT' },
  { id: 'waiting_internal', name: 'TICKETS.STATUS.WAITING_INTERNAL' },
  { id: 'resolved', name: 'TICKETS.STATUS.RESOLVED' },
  { id: 'closed', name: 'TICKETS.STATUS.CLOSED' },
];

const priorityOptions = [
  { id: '', name: 'TICKETS.FILTERS.ALL_PRIORITIES' },
  { id: 'urgent', name: 'TICKETS.PRIORITY.URGENT' },
  { id: 'high', name: 'TICKETS.PRIORITY.HIGH' },
  { id: 'medium', name: 'TICKETS.PRIORITY.MEDIUM' },
  { id: 'low', name: 'TICKETS.PRIORITY.LOW' },
];

const sortOptions = [
  { id: 'created_at_desc', name: 'TICKETS.SORT.NEWEST' },
  { id: 'created_at_asc', name: 'TICKETS.SORT.OLDEST' },
  { id: 'updated_at_desc', name: 'TICKETS.SORT.LAST_UPDATED' },
];

function emptyDraft() {
  return {
    status: '',
    priority: '',
    sort: 'created_at_desc',
    ticket_category_id: '',
    ticket_subcategory_id: '',
    team_id: '',
    explotacion: '',
    localidad: '',
    colonia: '',
  };
}

const subcategoriesForCategory = computed(() => {
  const cats = props.filterOptions?.categories || [];
  const cat = cats.find(c => String(c.id) === String(draft.value.ticket_category_id));
  return cat?.ticket_subcategories ?? [];
});

const coloniaOptions = computed(() => {
  const base = scopedColonias.value.length
    ? scopedColonias.value
    : props.filterOptions?.colonias || [];
  if (!draft.value.localidad) return base;
  return base.filter(
    c => String(c.localidad_id) === String(draft.value.localidad)
  );
});

const syncFromProps = () => {
  draft.value = { ...emptyDraft(), ...props.initialValues };
  scopedColonias.value = props.filterOptions?.colonias || [];
};

watch(() => props.initialValues, syncFromProps, { deep: true, immediate: true });

onMounted(() => {
  if (draft.value.explotacion || draft.value.localidad) {
    loadScopedCatalogs();
  }
});

const loadScopedCatalogs = async () => {
  if (!isContractsEnabled.value) return;
  isLoadingScoped.value = true;
  try {
    const { data } = await TicketsAPI.getFilterOptions({
      explotacion: draft.value.explotacion || undefined,
      localidad: draft.value.localidad || undefined,
    });
    scopedColonias.value = data.colonias || [];
  } catch {
    scopedColonias.value = props.filterOptions?.colonias || [];
  } finally {
    isLoadingScoped.value = false;
  }
};

watch(
  () => draft.value.explotacion,
  (next, prev) => {
    if (next === prev) return;
    draft.value.colonia = '';
    loadScopedCatalogs();
  }
);

watch(
  () => draft.value.localidad,
  (next, prev) => {
    if (next === prev) return;
    draft.value.colonia = '';
    if (draft.value.explotacion || next) {
      loadScopedCatalogs();
    }
  }
);

const clearFilters = () => {
  draft.value = emptyDraft();
  scopedColonias.value = props.filterOptions?.colonias || [];
};

const applyFilters = () => {
  emit('apply', { ...draft.value });
};

onBeforeUnmount(() => emit('close'));

const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleTicketFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="fixed z-[1200] w-[750px] max-w-[calc(100vw-1rem)] max-h-[min(70vh,32rem)] overflow-y-auto border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
    :style="popupStyle"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ $t('TICKETS.FILTER.TITLE') }}
    </h3>

    <p class="text-xs text-n-slate-11 -mt-4">
      {{ $t('TICKETS.FILTER.SUBTITLE') }}
    </p>

    <div class="grid gap-4 sm:grid-cols-2">
      <label class="grid gap-1">
        <span class="text-xs font-medium text-n-slate-11">{{
          $t('TICKETS.FILTER.STATUS_LABEL')
        }}</span>
        <select
          v-model="draft.status"
          class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
        >
          <option v-for="opt in statusOptions" :key="opt.id" :value="opt.id">
            {{ $t(opt.name) }}
          </option>
        </select>
      </label>

      <label class="grid gap-1">
        <span class="text-xs font-medium text-n-slate-11">{{
          $t('TICKETS.FILTER.PRIORITY_LABEL')
        }}</span>
        <select
          v-model="draft.priority"
          class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
        >
          <option v-for="opt in priorityOptions" :key="opt.id" :value="opt.id">
            {{ $t(opt.name) }}
          </option>
        </select>
      </label>

      <label class="grid gap-1">
        <span class="text-xs font-medium text-n-slate-11">{{
          $t('TICKETS.FILTER.SORT_LABEL')
        }}</span>
        <select
          v-model="draft.sort"
          class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
        >
          <option v-for="opt in sortOptions" :key="opt.id" :value="opt.id">
            {{ $t(opt.name) }}
          </option>
        </select>
      </label>

      <label v-if="showTeamFilter" class="grid gap-1">
        <span class="text-xs font-medium text-n-slate-11">{{
          $t('TICKETS.FILTERS.BY_TEAM')
        }}</span>
        <select
          v-model="draft.team_id"
          class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
        >
          <option
            v-for="opt in teamOptions"
            :key="String(opt.id)"
            :value="opt.id"
          >
            {{ opt.name }}
          </option>
        </select>
      </label>

      <label class="grid gap-1">
        <span class="text-xs font-medium text-n-slate-11">{{
          $t('TICKETS.FILTERS.CATEGORY')
        }}</span>
        <select
          v-model="draft.ticket_category_id"
          class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
          @change="draft.ticket_subcategory_id = ''"
        >
          <option value="">{{ $t('TICKETS.FILTERS.ALL_CATEGORIES') }}</option>
          <option
            v-for="cat in filterOptions.categories || []"
            :key="cat.id"
            :value="cat.id"
          >
            {{ cat.name }}
          </option>
        </select>
      </label>

      <label class="grid gap-1">
        <span class="text-xs font-medium text-n-slate-11">{{
          $t('TICKETS.FILTERS.SUBCATEGORY')
        }}</span>
        <select
          v-model="draft.ticket_subcategory_id"
          class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
          :disabled="!draft.ticket_category_id"
        >
          <option value="">{{ $t('TICKETS.FILTERS.ALL_SUBCATEGORIES') }}</option>
          <option
            v-for="sub in subcategoriesForCategory"
            :key="sub.id"
            :value="sub.id"
          >
            {{ sub.name }}
          </option>
        </select>
      </label>

      <template v-if="isContractsEnabled">
        <label class="grid gap-1 sm:col-span-2">
          <span class="text-xs font-medium text-n-slate-11">{{
            $t('TICKETS.FILTERS.EXPLOTACION')
          }}</span>
          <select
            v-model="draft.explotacion"
            class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
            :disabled="isLoadingScoped"
          >
            <option value="">{{ $t('TICKETS.FILTERS.ALL_EXPLOTACIONES') }}</option>
            <option
              v-for="ex in filterOptions.explotaciones || []"
              :key="ex.id"
              :value="ex.id"
            >
              {{ ex.name || ex.code }}
            </option>
          </select>
        </label>

        <label class="grid gap-1 sm:col-span-2">
          <span class="text-xs font-medium text-n-slate-11">{{
            $t('TICKETS.FILTERS.LOCALIDAD')
          }}</span>
          <select
            v-model="draft.localidad"
            class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
            :disabled="isLoadingScoped"
          >
            <option value="">{{ $t('TICKETS.FILTERS.ALL_LOCALIDADES') }}</option>
            <option
              v-for="loc in filterOptions.localidades || []"
              :key="loc.id"
              :value="loc.id"
            >
              {{ loc.name }}
            </option>
          </select>
        </label>

        <label class="grid gap-1 sm:col-span-2">
          <span class="text-xs font-medium text-n-slate-11">{{
            $t('TICKETS.FILTERS.COLONIA')
          }}</span>
          <select
            v-model="draft.colonia"
            class="px-2 py-1.5 text-xs rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
            :disabled="isLoadingScoped"
          >
            <option value="">{{ $t('TICKETS.FILTERS.ALL_COLONIAS') }}</option>
            <option
              v-for="c in coloniaOptions"
              :key="c.id"
              :value="c.id"
            >
              {{ c.name }}
            </option>
          </select>
          <span class="text-xxs text-n-slate-10">{{
            $t('TICKETS.FILTER.COLONIA_HINT')
          }}</span>
        </label>
      </template>
    </div>

    <div class="flex gap-2 justify-end">
      <Button sm faded slate @click="clearFilters">
        {{ $t('FILTER.CLEAR_BUTTON_LABEL') }}
      </Button>
      <Button sm solid blue @click="applyFilters">
        {{ $t('FILTER.SUBMIT_BUTTON_LABEL') }}
      </Button>
    </div>
  </div>
</template>
